import { gqlQuery, gqlMutate } from '@hapana/shared/web/services/requests.service'
import {
  AVAILABLE_TO_ASSIGN_SITES_MOCKED_RESPONSE,
  LOCATIONS_MOCKED_RESPONSE,
} from '_mocks/locations.mock'
import { SITE_USER_INSTRUCTORS_MOCK_RESPONSE } from '_mocks/instructors.mock'
import { SITE_CUSTOM_PROPERTIES_MOCK_RESPONSE } from '_mocks/site-custom-properties.mock'

import {
  sitesQuery,
  sitesPaginatedQuery,
  sitesCoreUserInstructorsQuery,
  siteCustomPropertiesQuery,
  availableSitesQuery,
  siteById,
  siteEnableAddOnsQuery,
  siteTopazIntegrationQuery,
  siteEnableMembershipPlanChangeQuery,
  siteEnableDoorAccessQuery,
  siteAdminSmsFromNameQuery,
  siteFobCredentialFormatQuery,
  siteEnableSharedSitesQuery,
  siteConnectedSharedSiteIdsQuery,
} from './graphql/sites.queries'

import {
  UPDATE_SITE_BUSINESS_CUSTOMIZATION,
  UPDATE_SITE_BUSINESS_LOCATIONS,
  UPDATE_SITE_SETTINGS_ACCOUNT_BILLING,
  UPDATE_SITE_REGIONAL_COMMUNICATION,
  TOGGLE_FRONT_DESK_KIOSK_BETA,
  UPDATE_SITE_CHECK_IN_SETTINGS,
  UPDATE_SITE_SETTINGS_SOCIAL_MEDIA,
  UPDATE_SITE_SETTINGS_ACCOUNT_POINT_OF_SALE,
} from './graphql/site.mutation'

const SITES = 'SITES'
const SITES_PAGINATED = 'SITES_PAGINATED'
const SITE = 'SITE'
const SITE_INSTRUCTORS = 'SITE_INSTRUCTORS'
const SITE_CUSTOM_PROPERTIES = 'SITE_CUSTOM_PROPERTIES'
const AVAILABLE_SITES = 'AVAILABLE_SITES'

export const getSites = gqlQuery({
  key: SITES,
  query: sitesQuery,
  mockResponse: LOCATIONS_MOCKED_RESPONSE,
  options: {
    isV1: false,
  },
})

export const getSitesPaginated = gqlQuery({
  key: SITES_PAGINATED,
  query: sitesPaginatedQuery,
  mockResponse: {
    sites: {
      results: [],
      pageInfo: {
        hasNextPage: false,
        endCursor: null,
        totalRecords: 0,
      },
    },
  },
  options: {
    isV1: false,
  },
})

export const getSite = gqlQuery({
  key: SITE,
  query: siteById,
  mockResponse: {
    site: {
      id: 'ccfdc896-e6d9-4b15-91ac-27878379cedc',
      name: 'Test Site',
      email: 'test@site.com',
      phone: '123-456-7890',
      status: 'ACTIVE',
      timezone: 'America/New_York',
      settings: {
        scheduler: {
          rules: {
            allowOverbooking: false,
            maxBookingDaysInAdvance: 30,
            cancellationPolicyHoursBeforeClass: 24,
            allowLateCancellations: false,
            cancellationPolicy: {
              allowLateCancellations: false,
              hoursBeforeClass: 24,
            },
            bookings: {
              autoConfirmRequests: true,
              autoDeductCredits: false,
              requestTimeSlotsDuration: 30,
              bookingNotifications: true,
              enableBookingRequests: true,
              enforceFixedBookingSlotIntervalsDuration: 30,
            },
            classes: {
              displayPriceColumn: false,
              instructorTypeFilter: false,
              sessionTypeFilter: false,
              roomTypeFilter: false,
              displayClassSize: false,
              soapNotes: false,
              displayCompletedClasses: false,
              defaultColor: '#3B82F6',
              daysInAdvance: 30,
            },
            reservations: {
              multipleReservations: false,
              daysInAdvance: null,
            },
            waitlist: {
              autoConfirmClients: false,
              manualMovements: false,
              lockPeriod: null,
              restrictions: null,
              promotion: null,
            },
            cancellation: {
              allowDeclineAndCancel: false,
              cancellationPolicies: false,
            },
          },
          sessionTypes: [],
          calendar: {
            defaultView: 'week',
            showInstructorNames: true,
            showCapacity: true,
          },
        },
      },
    },
  },
  options: {
    isV1: false,
  },
})

export const getAvailableSites = gqlQuery({
  key: AVAILABLE_SITES,
  query: availableSitesQuery,
  mockResponse: AVAILABLE_TO_ASSIGN_SITES_MOCKED_RESPONSE,
  options: {
    isV1: false,
  },
})

export const getSiteInstructors = gqlQuery({
  key: SITE_INSTRUCTORS,
  query: sitesCoreUserInstructorsQuery,
  mockResponse: SITE_USER_INSTRUCTORS_MOCK_RESPONSE,
  options: {
    isV1: false,
  },
})

export const getSiteCustomProperties = gqlQuery({
  key: SITE_CUSTOM_PROPERTIES,
  query: siteCustomPropertiesQuery,
  mockResponse: SITE_CUSTOM_PROPERTIES_MOCK_RESPONSE,
  options: {
    isV1: false,
  },
})

export const getSiteById = gqlQuery({
  key: AVAILABLE_SITES,
  query: siteById,
  options: {
    isV1: false,
  },
})

export const updateSiteBusinessCustomization = ({ siteId, input }) => {
  return gqlMutate({
    mutation: UPDATE_SITE_BUSINESS_CUSTOMIZATION,
    variables: {
      siteId,
      input,
    },
    mockResponse: {
      updateSiteSettingsBusinessCustomization: {
        id: siteId,
        settings: {},
      },
    },
    options: {
      isV1: false,
    },
  })
}

export const updateSiteBusinessLocations = ({ siteId, input }) => {
  return gqlMutate({
    mutation: UPDATE_SITE_BUSINESS_LOCATIONS,
    variables: {
      siteId,
      input,
    },
    mockResponse: {
      updateSiteSettingsBusinessLocations: {
        id: siteId,
        settings: {},
      },
    },
    options: {
      isV1: false,
    },
  })
}

export const updateSiteSettingsAccountBilling = ({ siteId, input }) => {
  return gqlMutate({
    mutation: UPDATE_SITE_SETTINGS_ACCOUNT_BILLING,
    variables: {
      siteId,
      input,
    },
    mockResponse: {
      updateSiteSettingsAccountBilling: {
        id: siteId,
        name: 'Downtown Gym',
        settings: {
          account: {
            billing: {
              retryFailedPayments: input.retryFailedPayments || {
                maxAttempts: 1,
                intervalInDays: 1,
              },
              transactionFeePreference:
                input.transactionFeePreference || 'Charge the fee to my business',
            },
          },
        },
      },
    },
    options: {
      isV1: false,
    },
  })
}

export const updateSiteRegionalCommunication = ({ siteId, input }) => {
  return gqlMutate({
    mutation: UPDATE_SITE_REGIONAL_COMMUNICATION,
    variables: {
      siteId,
      input,
    },
    mockResponse: {
      updateSiteSettingsRegionalCommunication: {
        id: siteId,
        settings: {},
      },
    },
    options: {
      isV1: false,
    },
  })
}

export const toggleFrontDeskKioskBeta = ({ siteId, enabled }) => {
  return gqlMutate({
    mutation: TOGGLE_FRONT_DESK_KIOSK_BETA,
    variables: { siteId, enabled },
    mockResponse: {
      toggleFrontDeskKioskBeta: {
        id: siteId,
        settings: {},
      },
    },
    options: { isV1: false },
  })
}

export const updateSiteSettingsCheckIn = ({ siteId, input }) => {
  return gqlMutate({
    mutation: UPDATE_SITE_CHECK_IN_SETTINGS,
    variables: { siteId, input },
    mockResponse: {
      updateSiteSettingsCheckIn: {
        id: siteId,
        settings: {
          location: {
            checkIn: {
              enableGeneralCheckIn: input.enableGeneralCheckIn,
              enableMobileClientCheckIn: input.enableMobileClientCheckIn,
              enableCheckInPopups: input.enableCheckInPopups,
              enableCheckInWidget: input.enableCheckInWidget,
              enableCheckInSoundAlerts: input.enableCheckInSoundAlerts,
              checkInMarksAttendance: input.checkInMarksAttendance,
              barcodeCheckInScreenRefreshPeriodInSeconds:
                input.barcodeCheckInScreenRefreshPeriodInSeconds,
              contactlessCheckIn: input.contactlessCheckIn,
            },
          },
        },
      },
    },
    options: { isV1: false },
  })
}

export const updateSiteSettingsSocialMedia = ({ siteId, input }) => {
  return gqlMutate({
    mutation: UPDATE_SITE_SETTINGS_SOCIAL_MEDIA,
    variables: { siteId, input },
    mockResponse: {
      updateSiteSettingsSocialMedia: {
        id: siteId,
        settings: {
          location: {
            socialMedia: {
              website: input.website,
              instagram: input.instagram,
              facebook: input.facebook,
              x: input.x,
            },
          },
        },
      },
    },
    options: { isV1: false },
  })
}

export const updateSiteSettingsAccountPointOfSale = ({ siteId, input }) => {
  return gqlMutate({
    mutation: UPDATE_SITE_SETTINGS_ACCOUNT_POINT_OF_SALE,
    variables: { siteId, input },
    mockResponse: {
      updateSiteSettingsAccountPointOfSale: {
        siteId,
        settings: {
          account: {
            pointOfSale: {
              enableCashDrawer: input.enableCashDrawer,
              itemTypes: input.itemTypes,
            },
          },
        },
      },
    },
    options: { isV1: false },
  })
}

export const getSiteEnableAddOns = gqlQuery({
  key: 'SITE_ENABLE_ADD_ONS',
  query: siteEnableAddOnsQuery,
})

export const getSiteTopazIntegration = gqlQuery({
  key: 'SITE_TOPAZ_INTEGRATION',
  query: siteTopazIntegrationQuery,
})

export const getSiteEnableMembershipPlanChange = gqlQuery({
  key: 'SITE_ENABLE_MEMBERSHIP_PLAN_CHANGE',
  query: siteEnableMembershipPlanChangeQuery,
})

export const getSiteEnableDoorAccess = gqlQuery({
  key: 'SITE_ENABLE_DOOR_ACCESS',
  query: siteEnableDoorAccessQuery,
})

export const getSiteAdminSmsFromName = gqlQuery({
  key: 'SITE_ADMIN_SMS_FROM_NAME',
  query: siteAdminSmsFromNameQuery,
})

export const getSiteFobCredentialFormat = gqlQuery({
  key: 'SITE_FOB_CREDENTIAL_FORMAT',
  query: siteFobCredentialFormatQuery,
})

export const getSiteEnableSharedSites = gqlQuery({
  key: 'SITE_ENABLE_SHARED_SITES',
  query: siteEnableSharedSitesQuery,
})

export const getSiteConnectedSharedSiteIds = gqlQuery({
  key: 'SITE_CONNECTED_SHARED_SITE_IDS',
  query: siteConnectedSharedSiteIdsQuery,
})
